import React, { useContext, type ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import { ButtonGroup } from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { typography } from '@atlaskit/theme';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { JswFreeConditionalRenderer } from '@atlassian/jira-jsw-feature-gates/src/ui/index.tsx';
import { PACKAGE_NAME } from '../../common/constants';
import type { AccessPageContext } from '../../common/types';
import { hasPermissionsGate, getMsgKeyByProjectType } from '../../common/utils';
import MainProviderContext from '../../services/access-page-context';
import AddPeople from './add-people';
import ManageRoles from './manage-roles';
import messages from './messages';
import type { Props, HeaderMessagesKeys } from './types';

export const Header = (props: Props) => {
	const {
		showBreadcrumbs,
		isError,
		isFetching,
		intl: { formatMessage },
		defaultRoleId,
		projectRoles,
		isProjectSimplified,
		accessType,
		onAddActorSuccess,
	} = props;

	const context: AccessPageContext = useContext(MainProviderContext);
	const { projectType } = context;
	const hasPermGate: boolean = hasPermissionsGate(context);

	const DEFAULT_HEADER_MSG_KEY: HeaderMessagesKeys = 'access';
	const HEADER_MSG_KEY: HeaderMessagesKeys = getMsgKeyByProjectType(
		DEFAULT_HEADER_MSG_KEY,
		context.projectType,
	);
	return (
		<Box xcss={fullWidthWrapperStyles}>
			<PageHeader
				truncateTitle
				disableTitleStyles
				actions={
					<ButtonGroup>
						<AddPeople
							hasPermissionsGate={hasPermGate}
							defaultRoleId={defaultRoleId}
							projectRoles={projectRoles}
							isError={isError}
							isFetching={isFetching}
							accessType={accessType}
							onAddActorSuccess={onAddActorSuccess}
							isProjectSimplified={isProjectSimplified}
						/>
						{isProjectSimplified && (
							<JswFreeConditionalRenderer
								// In JSW Free, the "Manage Roles" button is not shown
								renderJswFree={() => null}
								renderElse={() => (
									<ManageRoles
										hasPermissionsGate={hasPermGate}
										isError={isError}
										isFetching={isFetching}
										projectType={projectType}
									/>
								)}
								caller={PACKAGE_NAME}
							/>
						)}
					</ButtonGroup>
				}
				breadcrumbs={showBreadcrumbs ? <BreadcrumbRenderer /> : undefined}
			>
				<Heading>
					{formatMessage(
						// eslint-disable-next-line no-nested-ternary
						isProjectSimplified
							? messages[HEADER_MSG_KEY]
							: projectType === SERVICE_DESK_PROJECT &&
								  fg('jsm_simplified_project_settings_navigation')
								? messages.peopleAndAccess
								: messages.people,
					)}
				</Heading>
			</PageHeader>
		</Box>
	);
};

export default injectIntl(Header);

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.h1<{
	children?: ReactNode;
}>`
	${typography.h700};
`;

const fullWidthWrapperStyles = xcss({
	width: '100%',
});
